import { Inject, Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { Device } from '@ionic-native/device/ngx';
import { AppVersion } from '@ionic-native/app-version/ngx';
import { Platform } from '@ionic/angular';
import { catchError, map, mergeMap } from 'rxjs/operators';
import {
  DEFAULT_TAX_OFFLINE_SETTINGS,
  TaxOfflineSettings,
  normalizeTaxOfflineSettings,
} from '../common/tax/models/tax-offline-settings';
import { PRRO_LOG_LEVELS, PrroLogLevel } from './prro-log-queue.service';

export enum APP_CONFIG {
  FISCAL_REQUIRED = 'fiscal_required',
  ORDER_QUEUE_RETRY = 'order_queue_retry',
  SYNC_ACRHIVE_PERIOD = 'sync_archive_period_seconds',
  SYNC_START_ORDER = 'sync_start_order_queue',
  ORDER_QUEUE = 'order_queue',
  ORDER_QUEUE_CLOUD = 'order_queue_cloud',
  CHECK_ORDER_API = 'order_api',
  ORDER_SYNC_RETRY_API = 'order_sync_retry_api',
  ORDER_API = 'order_api',
  ORDER_SEND_API = 'order_send_api',
  ORDER_CHECK_API = 'order_check_api',
  PAYMENT_PROCESS_TYPE = 'payment_process_type',
  ACRHIVE_ORDERS_COUNT = 'archive_orders_count',
  TAX_CHECK_URL = 'tax_check_url',
  TAX_API_URL = 'tax_api_url',
  LOGGER_API_URL = 'logger_api_url',
  DEVICE_API_URL = 'device_api_url',
  TEST_TAX_SERVER = 'testTaxServer',
  CURRENT_VERSION = 'current_version',
  CURRENT_VERSION_IOS = 'current_version_ios',
  UPDATE_REQUIRED = 'update_required',
  HAS_TAX_TEST_SHIFT = 'has_tax_test_shift',
  SYNC_ORDER_TIMEOUT = 'sync_order_timeout',
  SYNC_ORDER_DISABLE = 'sync_order_disable',
  CHECK_CLOSED_ORDER_TIMEOUT = 'check_closed_order_timeout',
  FISCAL_SERVICE_OPERATIONS = 'fiscal_service_operations',
  API_ENDPOINT = 'api_endpoint',
  POS_API_ENDPOINT = 'pos_api_endpoint',
  CHECK_TERMINAL_DATA_ENDPOINT = 'check_terminal_data_endpoint',
  CHECK_CLOSED_ORDERS = 'check_closed_orders',
  ORDER_SYNC_INTERVAL = 'order_sync_interval',
  ORDER_SYNC_CHECKS = 'order_sync_checks',
  EXPIRENZA_API = 'expirenza_api',
  EXPIRENZA_HUB_ENDPOINT = 'expirenza_hub_endpoint',
  DISPLAY_LOGS = 'display_logs',
  POS_HUB_ENDPOINT = 'pos_hub_endpoint',
  TERMINAL_HEALTH_INTERVAL = 'terminal_health_interval',
  USE_REPORTS_NEW = 'use_reports_new',
  USE_TAX_REGISTER_CONTEXT = 'use_tax_register_context',
  TAX_OFFLINE_ENABLED = 'tax_offline_enabled',
  TAX_MAX_OFFLINE_DOCUMENTS = 'tax_max_offline_documents',
  TAX_MAX_OFFLINE_SESSION_DURATION_MINUTES = 'tax_max_offline_session_duration_minutes',
  TAX_OFFLINE_PACKAGE_DOCUMENTS = 'tax_offline_package_documents',
  TAX_CLEAR_OFFLINE_DOCUMENTS = 'tax_clear_offline_documents',
  TAX_RECOVERY_MODE = 'tax_recovery_mode',
  TAX_FAKE_PRRO = 'tax_fake_prro',
  TAX_AUTO_CLEAR_OFFLINE_DOCUMENTS = 'tax_auto_clear_offline_documents',
  TAX_DOC_TIMEOUT_SECONDS = 'tax_doc_timeout_seconds',
  TAX_SERVER_LOGS = 'tax_server_logs',
  TAX_SERVER_LOG_LEVEL = 'tax_server_log_level',
  FORCE_ONLINE_MODE = 'force_online_mode',
}

const _defaultConfig = {
  api_endpoint: 'https://api-v1.turbopos.net/api/v1',
  signal_r_endpoint: 'https://api-v1.turbopos.net/signalr/orderhub',
  [APP_CONFIG.POS_API_ENDPOINT]: 'https://pos-api-nx.turbopos.net/api',
  [APP_CONFIG.CHECK_TERMINAL_DATA_ENDPOINT]:
    'https://nt-pos-api-2.azurewebsites.net/api',
  fs_tax_doc: 'http://fs.tax.gov.ua:8609/fs/doc',
  fs_tax_pck: 'http://fs.tax.gov.ua:8609/fs/pck',
  fs_tax_cmd: 'http://fs.tax.gov.ua:8609/fs/cmd',
  logger_api:
    'http://nt-log-manager.azurewebsites.net/api/item?code=aah3OufGEeBhlHPxn_l6OS5vzMCcW_mrmGIpE2LfDASvAzFusJLX9g%3D%3D',
  order_queue:
    'http://ord-api.turbopos.net/api/order?code=rAe2hLBTNO_Awv7ShvzD8Pb3ROVSjHglKqQR8gVaiGPszCMJEAzFufjlgWA==',
  order_queue_retry:
    'http://ord-api.turbopos.net/api/order/retry?code=rAe2hLBTNO_Awv7ShvzD8Pb3ROVSjHglKqQR8gVaiGPszCMJEAzFufjlgWA==',
  order_queue_cloud:
    'https://nt-order-manager.azurewebsites.net/api/create?code=rAe2hLBTNO_Awv7ShvzDPb3ROVSKqQR8gVaiGPszCMJEAzFufjlgWA%3D%3D',
  [APP_CONFIG.ORDER_SEND_API]: '',
  [APP_CONFIG.ORDER_CHECK_API]: '',
  display_demo: false,
  [APP_CONFIG.TAX_CHECK_URL]: 'https://cabinet.tax.gov.ua/cashregs/check',
  [APP_CONFIG.TAX_API_URL]: 'http://tax-api.turbopos.net/api',
  [APP_CONFIG.LOGGER_API_URL]: 'http://lg.turbopos.net/api',
  [APP_CONFIG.DEVICE_API_URL]: 'https://device-api.turbopos.net',
  [APP_CONFIG.SYNC_ACRHIVE_PERIOD]: 1800,
  [APP_CONFIG.SYNC_START_ORDER]:
    'https://ord-api.turbopos.net/api/order/syncStart',
  [APP_CONFIG.PAYMENT_PROCESS_TYPE]: 'sync_order_with_verify',
  [APP_CONFIG.ACRHIVE_ORDERS_COUNT]: 300,
  store_api: 'https://store-api.turbopos.net/api',
  [APP_CONFIG.TEST_TAX_SERVER]: false,
  [APP_CONFIG.SYNC_ORDER_TIMEOUT]: 5000,
  [APP_CONFIG.SYNC_ORDER_DISABLE]: false,
  [APP_CONFIG.FISCAL_SERVICE_OPERATIONS]: false,
  [APP_CONFIG.CHECK_CLOSED_ORDERS]: true,
  [APP_CONFIG.ORDER_SYNC_INTERVAL]: 10000,
  [APP_CONFIG.ORDER_SYNC_CHECKS]: 10,
  [APP_CONFIG.CHECK_CLOSED_ORDER_TIMEOUT]: 3,
  [APP_CONFIG.EXPIRENZA_API]: 'API',
  [APP_CONFIG.EXPIRENZA_HUB_ENDPOINT]:
    'https://expirenza.turbopos.net/expirenzahub',
  http_timeout: 30,
  use_native_http: true,
  [APP_CONFIG.DISPLAY_LOGS]: false,
  [APP_CONFIG.POS_HUB_ENDPOINT]: 'https://pos-ws.turbopos.net/terminalHub',
  [APP_CONFIG.TERMINAL_HEALTH_INTERVAL]: 30000,
  [APP_CONFIG.USE_REPORTS_NEW]: true,
  [APP_CONFIG.USE_TAX_REGISTER_CONTEXT]: false,
  [APP_CONFIG.TAX_OFFLINE_ENABLED]: DEFAULT_TAX_OFFLINE_SETTINGS.offlineEnabled,
  [APP_CONFIG.TAX_MAX_OFFLINE_DOCUMENTS]:
    DEFAULT_TAX_OFFLINE_SETTINGS.maxOfflineDocuments,
  [APP_CONFIG.TAX_MAX_OFFLINE_SESSION_DURATION_MINUTES]:
    DEFAULT_TAX_OFFLINE_SETTINGS.maxOfflineSessionDurationMinutes,
  [APP_CONFIG.TAX_OFFLINE_PACKAGE_DOCUMENTS]:
    DEFAULT_TAX_OFFLINE_SETTINGS.offlinePackageDocumentLimit,
  // Shows the diagnostic action that deletes unsent local offline documents.
  [APP_CONFIG.TAX_CLEAR_OFFLINE_DOCUMENTS]: false,
  // Tax register recovery mode: offline work is never refused, and the upload sends documents
  // one by one, rebuilding those the server rejects.
  [APP_CONFIG.TAX_RECOVERY_MODE]: false,
  // fakePrro: a sale the tax register would fail (no context, or no real offline session can
  // start) continues in a fake offline session that the recovery upload sends later.
  [APP_CONFIG.TAX_FAKE_PRRO]: false,
  // For a device whose offline sequence is broken: each offline upload deletes the register's
  // offline documents instead and takes the server's state, so the register works online again.
  // fakePrro is off while it is on.
  [APP_CONFIG.TAX_AUTO_CLEAR_OFFLINE_DOCUMENTS]: false,
  // How long a document sent to the tax server online (/doc) may take; then the sale goes on
  // offline.
  [APP_CONFIG.TAX_DOC_TIMEOUT_SECONDS]: 5,
  // The tax registers' logs and states go to the server (Nt.Pos.Api) for the admin console.
  [APP_CONFIG.TAX_SERVER_LOGS]: true,
  // The lowest level of the logs sent: Information, Warning or Error.
  [APP_CONFIG.TAX_SERVER_LOG_LEVEL]: 'Information',
  // Tax registers send every document to the tax API: offline documents are ignored and no
  // offline session is started.
  [APP_CONFIG.FORCE_ONLINE_MODE]: false,
  euscp_signer_type: 'default',
};

const TAX_REGISTER_CONTEXT_ACTIVATED_KEY = '__tax_register_context_activated__';

// const fs_tax_cmd = 'http://fs.tax.gov.ua:8609/fs/cmd';
// const fs_tax_cmd = 'https://fs.tax.gov.ua:8643/fs/cmd';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private _protocol = window.location.protocol;
  private get _configApiUrl() {
    return `${this._protocol}//nt-platform-system.azurewebsites.net/api/HttpTrigger1?code=EgkT/BXwLyAjeUu35mIGbGayYTVujRvESL5BVuXWJS8Mk90Pd4S7Rw==`;
  }

  private configSubject = new BehaviorSubject<any>({ ..._defaultConfig });
  config = this.configSubject.asObservable();
  private _lastConfigUpdate: Date;
  private _configUpdateTimeoutSeconds = 60;

  get remoteCertificateUrl(): string | null {
    if (
      this.configSubject.value &&
      this.configSubject.value.remote_certificate_url
    ) {
      return this.configSubject.value.remote_certificate_url;
    }

    return null;
  }

  get usePrroServerTimeCorrection(): boolean {
    if (
      this.configSubject.value &&
      this.configSubject.value.euscp_signer_type
    ) {
      return this.configSubject.value.use_prro_server_time_correction;
    }

    return false;
  }

  // Offline work with tax registers, from the remote config; invalid values fall back to the
  // defaults and are capped at the API limits.
  get taxOfflineSettings(): TaxOfflineSettings {
    const config = this.configSubject.value || {};
    return normalizeTaxOfflineSettings({
      offlineEnabled: config[APP_CONFIG.TAX_OFFLINE_ENABLED],
      maxOfflineDocuments: config[APP_CONFIG.TAX_MAX_OFFLINE_DOCUMENTS],
      maxOfflineSessionDurationMinutes:
        config[APP_CONFIG.TAX_MAX_OFFLINE_SESSION_DURATION_MINUTES],
      offlinePackageDocumentLimit:
        config[APP_CONFIG.TAX_OFFLINE_PACKAGE_DOCUMENTS],
    });
  }

  get taxRecoveryMode(): boolean {
    const config = this.configSubject.value || {};
    return String(config[APP_CONFIG.TAX_RECOVERY_MODE]) === 'true';
  }

  // Off while tax_auto_clear_offline_documents is on.
  get taxFakePrro(): boolean {
    const config = this.configSubject.value || {};
    return (
      String(config[APP_CONFIG.TAX_FAKE_PRRO]) === 'true' &&
      !this.taxAutoClearOfflineDocuments
    );
  }

  get taxAutoClearOfflineDocuments(): boolean {
    const config = this.configSubject.value || {};
    return (
      String(config[APP_CONFIG.TAX_AUTO_CLEAR_OFFLINE_DOCUMENTS]) === 'true'
    );
  }

  // Seconds; an invalid value falls back to the default.
  get taxDocTimeoutSeconds(): number {
    const config = this.configSubject.value || {};
    const seconds = Number(config[APP_CONFIG.TAX_DOC_TIMEOUT_SECONDS]);
    return Number.isFinite(seconds) && seconds > 0
      ? seconds
      : _defaultConfig[APP_CONFIG.TAX_DOC_TIMEOUT_SECONDS];
  }

  get forceOnlineMode(): boolean {
    const config = this.configSubject.value || {};
    return String(config[APP_CONFIG.FORCE_ONLINE_MODE]) === 'true';
  }

  get taxServerLogs(): boolean {
    const config = this.configSubject.value || {};
    return String(config[APP_CONFIG.TAX_SERVER_LOGS]) !== 'false';
  }

  // An unknown level falls back to the default.
  get taxServerLogLevel(): PrroLogLevel {
    const config = this.configSubject.value || {};
    const level = String(config[APP_CONFIG.TAX_SERVER_LOG_LEVEL]).toLowerCase();
    return (
      PRRO_LOG_LEVELS.find((name) => name.toLowerCase() === level) ||
      (_defaultConfig[APP_CONFIG.TAX_SERVER_LOG_LEVEL] as PrroLogLevel)
    );
  }

  get api_endpoint(): string {
    // return 'http://localhost:53601/api/v1';
    if (this.configSubject.value && this.configSubject.value.api_endpoint) {
      return this.configSubject.value.api_endpoint;
    }

    return _defaultConfig.api_endpoint;
  }

  get euscp_signer_type(): string {
    if (
      this.configSubject.value &&
      this.configSubject.value.euscp_signer_type
    ) {
      return this.configSubject.value.euscp_signer_type;
    }

    return _defaultConfig.euscp_signer_type;
  }

  get display_demo(): boolean {
    if (this.configSubject.value) {
      return this.configSubject.value.display_demo;
    }

    return _defaultConfig.display_demo;
  }

  get display_logs(): boolean {
    if (this.configSubject.value) {
      return this.configSubject.value.display_logs;
    }

    return _defaultConfig.display_logs;
  }

  get use_reports_new(): boolean {
    if (this.configSubject.value) {
      return !!this.configSubject.value.use_reports_new;
    }

    return _defaultConfig.use_reports_new;
  }

  private _taxRegisterContextActivated: boolean | null = null;

  get useTaxRegisterContext(): boolean {
    if (this.configSubject.value) {
      return !!this.configSubject.value.use_tax_register_context;
    }

    return !!_defaultConfig.use_tax_register_context;
  }

  get signal_r_endpoint(): string {
    if (
      this.configSubject.value &&
      this.configSubject.value.signal_r_endpoint
    ) {
      return this.configSubject.value.signal_r_endpoint;
    }

    return _defaultConfig.signal_r_endpoint;
  }

  get pos_api_endpoint(): string {
    // return 'https://localhost:7024/api';
    if (this.configSubject.value && this.configSubject.value.pos_api_endpoint) {
      return this.configSubject.value.pos_api_endpoint;
    }

    return _defaultConfig.pos_api_endpoint;
  }

  get fs_tax_doc(): string {
    if (this.configSubject.value && this.configSubject.value.fs_tax_doc) {
      return this.configSubject.value.fs_tax_doc;
    }

    return _defaultConfig.fs_tax_doc;
  }

  get fs_tax_pck(): string {
    if (this.configSubject.value && this.configSubject.value.fs_tax_pck) {
      return this.configSubject.value.fs_tax_pck;
    }

    return _defaultConfig.fs_tax_pck;
  }

  get fs_tax_cmd(): string {
    if (this.configSubject.value && this.configSubject.value.fs_tax_cmd) {
      return this.configSubject.value.fs_tax_cmd;
    }

    return _defaultConfig.fs_tax_cmd;
  }

  // get logger_api(): string {
  //   if (this.configSubject.value && this.configSubject.value.logger_api) {
  //     return this.configSubject.value.logger_api;
  //   }

  //   return _defaultConfig.logger_api;
  // }

  get order_queue(): string {
    if (this.configSubject.value && this.configSubject.value.order_queue) {
      return this.configSubject.value.order_queue;
    }

    return _defaultConfig.order_queue;
  }

  get store_api(): string {
    if (this.configSubject.value && this.configSubject.value.store_api) {
      return this.configSubject.value.store_api;
    }

    return _defaultConfig.store_api;
  }

  private _http: HttpClient;
  get httpTimeout() {
    if (this.configSubject.value && this.configSubject.value.http_timeout) {
      return this.configSubject.value.http_timeout;
    }

    return _defaultConfig.http_timeout;
  }

  get useNativeHttp() {
    if (this.configSubject.value && this.configSubject.value.use_native_http) {
      return this.configSubject.value.use_native_http;
    }

    return _defaultConfig.use_native_http;
  }

  private _defaultStoreSettings = {
    enabled: false,
    hasloyalty: false,
    hasmenu: false,
    id: '',
    imagebase64: '',
    imageurl: '',
    name: '',
    storesite: '',
    storeurl: '',
    unauthorizedorders: false,
  };

  private _storeSettings: {
    enabled: boolean;
    hasloyalty: boolean;
    hasmenu: boolean;
    id: string;
    imagebase64: string;
    imageurl: string;
    name: string;
    storesite: string;
    storeurl: string;
    unauthorizedorders: boolean;
  };
  saveStoreSettings(_storeSettings: {
    enabled: boolean;
    hasloyalty: boolean;
    hasmenu: boolean;
    id: string;
    imagebase64: string;
    imageurl: string;
    name: string;
    storesite: string;
    storeurl: string;
    unauthorizedorders: boolean;
  }) {
    this._storeSettings = _storeSettings;
  }

  getStoreSettings() {
    return this._storeSettings || this._defaultStoreSettings;
  }

  constructor(
    private _httpBackend: HttpBackend,
    private _device: Device,
    @Inject('ENVIRONMENT') private environment: any,
    private appVersion: AppVersion,
    private _platform: Platform,
    private _httpClient: HttpClient,
  ) {
    this._http = new HttpClient(this._httpBackend);

    this._platform.ready().then((platform) => {
      if (platform != 'ios') {
        this._protocol = 'https:';
      }
    });
  }

  getValue(key: string) {
    if (
      this.configSubject.value &&
      this.configSubject.value[key] !== undefined
    ) {
      return this.configSubject.value[key];
    }

    return _defaultConfig[key];
  }

  getSystemConfig(forse = true): Observable<void> {
    if (forse) {
      return this._getSystemConfig().pipe(
        map((_config) => {
          this._applyRemoteConfig(_config);
        }),
        catchError(() => of(null)),
      );
    } else {
      return of(null);
    }
  }

  private _getSystemConfig(): Observable<any> {
    const env = this.environment.production ? 'production' : 'debug';
    return from(this._platform.ready()).pipe(
      mergeMap((platf) => {
        if (platf != 'ios') {
          this._protocol = 'https:';
        }
        return this._http.get<any>(
          `${this._configApiUrl}&imei=${this._device.uuid}&serial=${this._device.serial}&env=${env}&platform=${platf}&schema=${this._protocol}`,
        );
      }),
    );
  }

  private _applyRemoteConfig(remote: any): any {
    let parsed = remote;
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        parsed = null;
      }
    }

    if (
      parsed &&
      parsed.data &&
      typeof parsed.data === 'object' &&
      !parsed.api_endpoint
    ) {
      parsed = parsed.data;
    }

    if (!parsed || typeof parsed !== 'object') {
      return this.configSubject.value;
    }

    const merged = { ..._defaultConfig, ...parsed };
    this.configSubject.next(merged);
    this._lastConfigUpdate = new Date();
    return merged;
  }

  // Creates/updates this terminal's row in the admin terminal list (Nt.Terminal PosTerminal table).
  async updateDeviceState(data?: any) {
    // Before the platform is ready a native device has no uuid yet and the
    // fallback id differs from the one the WebSocket connects with later.
    await this._platform.ready();

    const config = this.configSubject.value || _defaultConfig;
    this._getSystemConfig();
    // if (data) {
    //   Object.assign(config, data);
    // }

    config.protocol = this._protocol;
    config.version = this.environment.version;

    const model = {
      imei: this._device.uuid,
      platform: this._device.platform,
      dataJson: JSON.stringify(config),
      version: this.environment.version,
      platformVersion: this._device.version,
      appVersion: this.environment.version,
      model: this._device.model,
      manufacturer: this._device.manufacturer || '',
    };

    if (this._device.platform != 'web_browser') {
      try {
        model.version = await this.appVersion.getVersionNumber();
        model.appVersion = model.version;
      } catch (err) {
        console.error('Device state: app version unavailable', err);
      }
    }

    this._httpClient
      .put<any>(`${this.pos_api_endpoint}/terminal/state`, model)
      .subscribe({
        error: (err) =>
          console.error(`Device state for ${model.imei} was not saved`, err),
      });
  }

  async registerDevice(companyId?: string) {
    const model: any = {};
    model.companyId = companyId;
    model.model = this._device.model;
    model.platform = this._device.platform;
    model.uuid = this._device.uuid;
    model.version = this._device.version;
    model.manufacturer = this._device.manufacturer;
    model.isVirtual = this._device.isVirtual;
    model.serial = this._device.serial;
    model.appName = 'nt.pos.terminal';

    if (this._device.platform == 'web_browser') {
      model.appVersion = this.environment.version;
    } else {
      model.appVersion = await this.appVersion.getVersionNumber();
    }

    this._registerDevice(model).subscribe();
  }

  private _registerDevice(model: any) {
    return this._http.post(
      this.api_endpoint + '/app-client/company/register-device',
      model,
    );
  }

  private _requestSystemConfig(): Observable<any> {
    const env = this.environment.production ? 'production' : 'debug';
    return from(this._platform.ready()).pipe(
      mergeMap((platf) => {
        if (platf != 'ios') {
          this._protocol = 'https:';
        }
        return this._http.get<any>(
          `${this._configApiUrl}&imei=${this._device.uuid}&serial=${this._device.serial}&env=${env}&platform=${platf}&schema=${this._protocol}`,
        );
      }),
    );
  }

  private _getSystemConfigByTimeout(): Observable<any> {
    const _now = new Date();
    const lastUpdateBeforeSeconds = this._lastConfigUpdate
      ? Math.floor((_now.getTime() - this._lastConfigUpdate.getTime()) / 1000)
      : this._configUpdateTimeoutSeconds + 1;

    const isConfigExpired =
      lastUpdateBeforeSeconds > this._configUpdateTimeoutSeconds;

    if (this._lastConfigUpdate && !isConfigExpired) {
      return of(this.configSubject.value);
    }

    return this._requestSystemConfig().pipe(
      mergeMap((_config) => {
        if (!_config) {
          return of(this.configSubject.value);
        }

        return of(this._applyRemoteConfig(_config));
      }),
      catchError(() => of(this.configSubject.value)),
    );
  }

  async _getValue<T>(key: APP_CONFIG): Promise<T> {
    const _config = await this._getSystemConfigByTimeout().toPromise();
    return _config[key] || _defaultConfig[key];
  }
}
